document.addEventListener("DOMContentLoaded", () => {
  initPartnerBrandsFilter();
  initPartnerBrandCardClicks();
});

/**
 * Partner Brands Filter
 * Allows users to filter brand cards by multiple categories
 * Supports dynamic filter text updates and discount-based sorting
 */
function initPartnerBrandsFilter() {
  const checkboxes = document.querySelectorAll(
    'input[name="partner-brand-filter"]'
  );
  const cards = Array.from(document.querySelectorAll(".partner-brands__card"));
  const details = document.querySelector(".partner-brands__filter-details");
  const filterSelectedText = document.querySelector(
    ".partner-brands__filter-selected"
  );
  const cardsContainer = document.querySelector(".partner-brands__cards");

  // Exit if elements don't exist
  if (!checkboxes.length || !cards.length) return;

  // Track selection order for display
  const selectionOrder = [];

  // Sort cards by discount on page load
  sortCardsByDiscount();

  // Add change listener to all checkboxes
  checkboxes.forEach((checkbox) =>
    checkbox.addEventListener("change", handleFilterChange)
  );

  /**
   * Handle checkbox state changes
   */
  function handleFilterChange(e) {
    const checkbox = e.target;

    // If "All" is checked, uncheck all other filters
    if (checkbox.value === "all" && checkbox.checked) {
      checkboxes.forEach((cb) => {
        if (cb.value !== "all") cb.checked = false;
      });
      selectionOrder.length = 0; // Clear selection order
    }

    // If any specific filter is checked, uncheck "All"
    if (checkbox.value !== "all" && checkbox.checked) {
      checkboxes.forEach((cb) => {
        if (cb.value === "all") cb.checked = false;
      });
      // Add to selection order if not already present
      if (!selectionOrder.includes(checkbox.value)) {
        selectionOrder.push(checkbox.value);
      }
    }

    // If a filter is unchecked, remove from selection order
    if (!checkbox.checked && checkbox.value !== "all") {
      const index = selectionOrder.indexOf(checkbox.value);
      if (index > -1) {
        selectionOrder.splice(index, 1);
      }
    }

    // Apply the filter and update text
    filterCards();
    updateFilterText();
  }

  /**
   * Show/hide cards based on selected filters
   * Supports multiple categories per card
   */
  function filterCards() {
    const selectedFilters = Array.from(checkboxes)
      .filter((cb) => cb.checked && cb.value !== "all")
      .map((cb) => cb.value);

    // Show all cards if no specific filters selected
    if (selectedFilters.length === 0) {
      cards.forEach((card) => card.classList.remove("hidden"));
      return;
    }

    // Show only cards matching ANY of the selected filters
    cards.forEach((card) => {
      const categories = card.dataset.category.split(",").map((c) => c.trim());
      const matches = categories.some((cat) => selectedFilters.includes(cat));
      card.classList.toggle("hidden", !matches);
    });
  }

  /**
   * Update filter text to show selected categories in selection order
   */
  function updateFilterText() {
    if (selectionOrder.length === 0) {
      filterSelectedText.textContent = "All";
      return;
    }

    // Map selection order to display names
    const displayNames = selectionOrder.map((value) => {
      const checkbox = Array.from(checkboxes).find((cb) => cb.value === value);
      return checkbox
        ? checkbox.closest("label").querySelector("span").textContent
        : "";
    });

    filterSelectedText.textContent = displayNames.join(", ");
  }

  /**
   * Parse discount text to extract type and value
   */
  function parseDiscount(text) {
    // Special case: "100% Off Basic Membership" should be in "other" category
    if (text.includes("100%") && text.includes("Membership")) {
      return { type: "other", value: text };
    }

    // Check for dollar amount (e.g., "$35 Off")
    const dollarMatch = text.match(/\$(\d+)/);
    if (dollarMatch) {
      return { type: "numeric", unit: "dollar", value: parseInt(dollarMatch[1]) };
    }

    // Check for percentage (e.g., "25% Off")
    const percentMatch = text.match(/(\d+)%/);
    if (percentMatch) {
      return { type: "numeric", unit: "percent", value: parseInt(percentMatch[1]) };
    }

    // Everything else (e.g., "Buy 2 Get 1 Free")
    return { type: "other", value: text };
  }

  /**
   * Sort cards by discount:
   * 1. Numeric discounts (dollars & percentages mixed by value, largest first; dollar wins ties)
   * 2. Other (alphabetical)
   */
  function sortCardsByDiscount() {
    cards.sort((a, b) => {
      const aText = a.querySelector(".ob-body").textContent.trim();
      const bText = b.querySelector(".ob-body").textContent.trim();
      const aDiscount = parseDiscount(aText);
      const bDiscount = parseDiscount(bText);

      // Numeric discounts first (largest first)
      if (aDiscount.type === "numeric" && bDiscount.type !== "numeric")
        return -1;
      if (aDiscount.type !== "numeric" && bDiscount.type === "numeric")
        return 1;
      if (aDiscount.type === "numeric" && bDiscount.type === "numeric") {
        if (aDiscount.value !== bDiscount.value)
          return bDiscount.value - aDiscount.value; // Descending
        if (aDiscount.unit !== bDiscount.unit)
          return aDiscount.unit === "dollar" ? -1 : 1;
        return 0;
      }

      // Other (alphabetical)
      return aText.localeCompare(bText);
    });

    // Re-append cards in sorted order
    cards.forEach((card) => cardsContainer.appendChild(card));
  }

  // Close dropdown when clicking outside
  if (details) {
    document.addEventListener("click", (e) => {
      if (details.open && !details.contains(e.target)) {
        details.open = false;
      }
    });

    // Close dropdown on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && details.open) {
        details.open = false;
      }
    });
  }

  // Initialize
  filterCards();
  updateFilterText();
}

/**
 * Make the entire partner brand card clickable without affecting nested links
 */
function initPartnerBrandCardClicks() {
  const cards = document.querySelectorAll(".partner-brands__card");
  if (!cards.length) return;

  cards.forEach((card) => {
    const productLink = card.querySelector(".partner-brands__product-link");
    if (!productLink) return;

    const rawHref = productLink.getAttribute("href");
    if (!rawHref || rawHref === "#!" || rawHref === "#") return;

    const cardUrl = productLink.href;

    if (!card.hasAttribute("tabindex")) {
      card.setAttribute("tabindex", "0");
    }

    card.addEventListener("click", (event) => {
      const interactive = event.target.closest("a, button, [data-interactive]");
      if (interactive) return;

      window.open(cardUrl, "_blank", "noopener");
    });

    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;

      event.preventDefault();
      window.open(cardUrl, "_blank", "noopener");
    });
  });

  const nestedLinks = document.querySelectorAll(".partner-brands__card a");
  nestedLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      event.stopPropagation();
    });

    link.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.stopPropagation();
      }
    });
  });
}
